import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  QueryDefinition,
} from '@reduxjs/toolkit/dist/query';
import { QueryActionCreatorResult } from '@reduxjs/toolkit/dist/query/core/buildInitiate';
import { CustomPropertiesType, DateTimeString, GenericObject } from 'types';
import {
  UseMutation,
  UseQuery,
} from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { MutationDefinition } from '@reduxjs/toolkit/dist/query/react';
import { Tag } from 'types/clientV2/tag';
import { UserLite } from 'types/user';
import { TeamLite } from 'types/properties';
import {
  UnifiedDELETERequest,
  UnifiedDELETEResponse,
  UnifiedPATCHRequest,
  UnifiedPATCHResponse,
  UnifiedQueryParams,
  UnifiedToggleRequest,
  UnifiedToggleResponse,
} from 'types/unified/api';

export enum QueryFilterOperators {
  '=' = '=',
  '>' = '>',
  '>=' = '>=',
  '<' = '<',
  '<=' = '<=',
  'is' = 'is',
  'equals' = 'equals',
  'after' = 'after',
  'onOrAfter' = 'onOrAfter',
  'before' = 'before',
  'onOrBefore' = 'onOrBefore',
  'contains' = 'contains',
  'startsWith' = 'startsWith',
  'endsWith' = 'endsWith',
  'isEmpty' = 'isEmpty',
  'isNotEmpty' = 'isNotEmpty',
  'isAnyOf' = 'isAnyOf',
  'not' = 'not',
  '!=' = '!=',
}

export const OperatorMap: Record<QueryFilterOperators, string> = {
  [QueryFilterOperators['=']]: '',
  [QueryFilterOperators['>']]: '__gt',
  [QueryFilterOperators['>=']]: '__gte',
  [QueryFilterOperators['<']]: '__lt',
  [QueryFilterOperators['<=']]: '__lte',
  [QueryFilterOperators.is]: '__iexact',
  [QueryFilterOperators.equals]: '__iexact',
  [QueryFilterOperators.after]: '__gt',
  [QueryFilterOperators.onOrAfter]: '__gte',
  [QueryFilterOperators.before]: '__lt',
  [QueryFilterOperators.onOrBefore]: '__lte',
  [QueryFilterOperators.contains]: '__icontains',
  [QueryFilterOperators.startsWith]: '__istartswith',
  [QueryFilterOperators.endsWith]: '__iendswith',
  [QueryFilterOperators.isEmpty]: '__isnull',
  [QueryFilterOperators.isNotEmpty]: '__isnull', // the apparent duplication is intentional, see the NullOperatorValues enum
  [QueryFilterOperators.isAnyOf]: '__in',
  [QueryFilterOperators.not]: '',
  [QueryFilterOperators['!=']]: '',
} as const;

export enum NullOperatorValues {
  'isEmpty' = 'true',
  'isNotEmpty' = 'false',
}

export enum OrderDirectionValues {
  asc = '',
  desc = '-',
}

export enum FilterLinkOperatorsValues {
  and = ',',
  or = '|',
}

export const ALL = '__all__';

/**
 * Represents the core fields for a Unified API schema.
 *
 * Core fields are a predefined subset of fields defined on each schema backend.
 * Unlike `__all__`, `__core__` acts as a keyword replacement for a specific set of fields.
 *
 * @example Basic usage
 * // Returns all core fields (non-expanded)
 * schema: '__core__'
 *
 * @example With additional fields
 * // Returns all core fields + internalMetadata
 * schema: ['__core__', 'internalMetadata']
 *
 * @example Shorthand for nested core
 * // Empty object is shorthand for nested __core__
 * schema: ['__core__', { lines: [] }]
 */
export const CORE = '__core__';

/**
 * Use this constant to remove pagination limits entirely.
 * @warning This will fetch ALL records and may cause performance issues with large datasets.
 * Only use when you are certain about the maximum possible size of the result set.
 * With great power comes great responsibility.
 */
export const DISABLE_PAGE_LIMIT = 0;

type commaSeparatedString<T> = keyof T extends infer K
  ? K extends string
    ? K extends keyof T
      ? `${K}${Exclude<commaSeparatedStringExclude<T, K>, ''> extends never
          ? ''
          : ','}${Exclude<commaSeparatedStringExclude<T, K>, ''>}`
      : never
    : never
  : never;

type commaSeparatedStringExclude<T, U> = keyof T extends infer K
  ? K extends string
    ? U extends K
      ? never
      : K
    : never
  : never;

type SubSchema<T> = T extends (infer U)[]
  ? SubSchema<U>[]
  : T extends null | undefined
  ? never
  : NonNullable<T> extends object
  ?
      | keyof NonNullable<T>
      | { [key in keyof NonNullable<T>]?: SubSchema<NonNullable<T>[key]>[] }
  : never;

/**
 * NestedPaths
 * Get all the possible paths of an object
 * @example
 * type Keys = NestedPaths<{ a: { b: { c: string } }>
 * // 'a' | 'a.b' | 'a.b.c'
 */
export type NestedPaths<T extends GenericObject> = {
  [K in keyof T]: NonNullable<T[K]> extends (infer U)[]
    ? U extends GenericObject
      ? K | `${string & K}.${string & NestedPath<U>}`
      : K
    : NonNullable<T[K]> extends GenericObject
    ? K | `${string & K}.${string & NestedPath<NonNullable<T[K]>>}`
    : K;
}[keyof T];

// Update NestedPath to handle nullable fields
export type NestedPath<T extends GenericObject> = {
  [K in keyof T]: NonNullable<T[K]> extends GenericObject
    ? K | `${string & K}`
    : K;
}[keyof T];

export type NestedTypeField<T extends GenericObject> = keyof T | NestedPaths<T>;

/**
 * FieldFilter
 * @example
 * type FieldFilter = FieldFilter<StockLot>
 * // { field: 'id' | 'org' | 'status' | 'quant' | 'part.id' | 'part.mpn', operator: '=', value: any }
 */
export type FieldFilter<T extends GenericResource> = {
  field: NestedTypeField<T>;
  operator: keyof typeof QueryFilterOperators;
  value: unknown;
};

export interface QueryParams<T extends GenericResource> {
  pageSize?: number | typeof DISABLE_PAGE_LIMIT;
  pageNumber?: number;
  // order by can be any property of T for example QueryParams<StockLot> can be {order_by: 'quantity'}
  sort?: {
    orderBy: keyof T;
    orderDirection: keyof typeof OrderDirectionValues;
  }[];
  // filter is an array of objects with keys of T and any value
  filters?:
    | []
    | [FieldFilter<T>]
    | [
        FieldFilter<T>,
        ...(FieldFilter<T> | keyof typeof FilterLinkOperatorsValues)[],
        FieldFilter<T>
      ];
  excludes?:
    | []
    | [FieldFilter<T>]
    | [
        FieldFilter<T>,
        ...(FieldFilter<T> | keyof typeof FilterLinkOperatorsValues)[],
        FieldFilter<T>
      ];
  // schema is an array of strings or objects with keys of T and values of strings or objects with keys of T and values of strings
  schema?:
    | keyof T
    | (
        | keyof T
        | typeof ALL
        | {
            [key in keyof T]?: SubSchema<T[key]>[];
          }
      )[]
    | commaSeparatedString<T>;
  noCache?: boolean;
  newVersion?: boolean;
  blended?: boolean;
  search?: string;
  searchSchema?:
    | keyof T
    | (
        | keyof T
        | typeof ALL
        | {
            [key in keyof T]?: SubSchema<T[key]>[];
          }
      )[]
    | commaSeparatedString<T>;
  overrideOrgId?: string;
  exportBlob?: boolean;
  distinct?: boolean;
}

export interface ListResponse<T> {
  count: number;
  pages: number;
  data: T[];
}

/**
 * Generic type for creating POST request schemas from base schemas.
 * Takes a base schema T and optional fields O to create a POST schema with:
 * - Required fields specified by K (with Lite applied to object fields)
 * - Optional fields specified by O (with Lite applied to object fields)
 * - All other fields excluded
 * - ID field always omitted
 * - Fields specified in Preserve will keep their original types without Lite transformation
 *
 * This type strictly enforces that only fields specified in K or O can be included,
 * preventing any other fields from being passed.
 *
 * @example
 * // With only optional fields
 * type CreateKitRequest = POSTSchema<
 *   KitRequestSchema,
 *   never,  // no required fields
 *   'internalNotes' | 'externalNotes' | 'shipPartial'  // optional fields
 * >;
 *
 * // With both required and optional fields
 * type CreateKitRequest = POSTSchema<
 *   KitRequestSchema,
 *   'name',  // required fields
 *   'internalNotes' | 'externalNotes' | 'shipPartial'  // optional fields
 * >;
 *
 * // With preserved fields that should not be transformed
 * type CreateSupplierQuoteLine = POSTSchema<
 *   SupplierQuoteLineSchema,
 *   'supplierQuote',  // required fields
 *   'prices' | 'moq' | 'multiple',  // optional fields
 *   'prices'  // preserve original type for prices field
 * >;
 */
export type POSTSchema<
  T extends GenericResource,
  K extends Exclude<keyof T, 'id'>,
  O extends Exclude<keyof T, K | 'id'> = never,
  Preserve extends keyof T = never
> = Required<Lite<Pick<T, K>, Extract<Preserve, keyof Pick<T, K>>>> &
  Partial<Lite<Pick<T, O>, Extract<Preserve, keyof Pick<T, O>>>> & {
    [P in Exclude<keyof T, K | O | 'id'>]?: never;
  };

export type PATCHSchema<T> = Partial<T> & {
  id: string;
};

export type UUID = string;

// Generic type representing a resource with an ID field
export interface GenericResource extends GenericObject {
  id: UUID;
}

export interface GenericRecord extends GenericResource {
  customProperties?: CustomPropertiesType;
  tags?: Tag[];
  approvedAt?: DateTimeString | null;
  approvedBy?: UserLite | null;
  lockedAt?: DateTimeString | null;
  lockedBy?: UserLite | null;
  archivedAt?: DateTimeString | null;
  archivedBy?: UserLite | null;
  voidedAt?: DateTimeString | null;
  voidedBy?: UserLite | null;
  createdAt?: DateTimeString | null;
  createdBy?: UserLite | null;
  updatedAt?: DateTimeString | null;
  team?: TeamLite | null;
  dataSource?: string | null;
  dataSourceSyncedAt?: DateTimeString | null;
  externallySynced?: boolean;
}

export type ClientV2POSTRequest<T> = T | T[];

export type ClientV2PATCHRequest<T extends GenericResource> =
  | Partial<T>
  | Partial<T>[];

export type ClientV2DELETERequest<T extends GenericResource> = {
  ids: T['id'][];
};

export interface ClientV2POSTResponse<T extends GenericResource> {
  createdIds: T['id'][];
}

export interface ClientV2PATCHResponse<T extends GenericResource> {
  updatedIds: T['id'][];
  nonUpdatedIds: T['id'][];
}

export interface ClientV2DELETEResponse<T extends GenericResource> {
  deletedIds: T['id'][];
}

export interface ClientV2ToggleResponse<T extends GenericResource> {
  updatedIds: T['id'][];
  nonUpdatedIds: T['id'][];
}

export interface ClientV2BulkActionRequest<T extends GenericResource> {
  ids: T['id'][];
}

export type ClientV2BulkActionWithArgsRequest<
  T extends GenericResource,
  O extends keyof T = never
> = {
  ids: T['id'][];
} & Partial<Pick<T, O>>;

// Used for Approve/Unapprove, Lock/Unlock, Archive/Unarchive request bodies
export interface ClientV2ToggleRequest<T extends GenericResource> {
  ids: T['id'][];
}

export interface AsyncJob {
  id: string;
}

export enum AsyncJobStatus {
  starting = 'starting',
  processing = 'processing',
  done = 'done',
  error = 'error',
  running = 'running',
}

export interface AsyncJobResponse<T> {
  jobId: string;
  status: AsyncJobStatus;
  data: T;
  message: string | null;
}

/**
 * Transforms a type by replacing object properties with their string representation, typically used for ID fields.
 * If the property is an array of objects, it replaces the array with an array of strings.
 *
 * The transformation is done as follows:
 * - If a property is an object and not part of the keys specified in `K`, it is replaced with a string.
 * - If a property is an array of objects and not part of the keys specified in `K`, it is replaced with an array of strings.
 * - If a property is an array but not of objects, it remains the same.
 * - All other properties remain the same.
 * - If a property key is part of `K`, it will not be transformed and will retain its original type.
 *
 * This is useful when you want to simplify complex objects or arrays of objects to their ID representations which is often how Flagship will represent its query schema.
 *
 * @example
 * type AllocationQuerySchema = { id: string, org: Org, stockLot: { id: string, name: string }, productionRun: { id: string, name: string }[] };
 * type SimplifiedAllocationQuerySchema = Lite<AllocationQuerySchema, 'stockLot'>; // { id: string, org: string, stockLot: { id: string, name: string }, productionRun: string[] }
 *
 * @example
 * type AllocationQuerySchemaWithArray = { id: string, org: Org, stockLot: { id: string, name: string }[], productionRun: { id: string, name: string }[] };
 * type SimplifiedAllocationQuerySchemaWithArray = Lite<AllocationQuerySchemaWithArray, 'stockLot'>; // { id: string, org: string, stockLot: { id: string, name: string }[], productionRun: string[] }
 */
export type Lite<T, K extends keyof T = never> = {
  [P in keyof T]: P extends K
    ? T[P]
    : T[P] extends Array<infer U>
    ? U extends object
      ? string[]
      : T[P]
    : T[P] extends object
    ? string
    : T[P] extends object | null
    ? string | null
    : T[P] extends object | undefined
    ? string | undefined
    : T[P] extends object | null | undefined
    ? string | null | undefined
    : T[P];
};

/**
 * `PropertySubsetValidator` is a utility type that creates a strict subset of properties from type `T`.
 *
 * It takes two type parameters:
 * - `T`: The original type.
 * - `K`: The keys of `T` that we want to allow access to.
 *
 * This type will only allow access to the specified keys `K` and will error when trying to access any other properties.
 *
 * @example
 * type User = { name: string, age: number, email: string };
 * type ValidUserProps = PropertySubsetValidator<User, 'name' | 'age'>; // Only allows access to 'name' and 'age'
 *
 * // Usage in function parameters to ensure correct type:
 * function doSomething(
 *   allocations:
 *     | PropertySubsetValidator<AllocationQuerySchema, 'name'>[]
 *     | PropertySubsetValidator<AllocationCRUDSchema, 'name'>[]
 * ) {
 *   return allocations.filter((allocation) => allocation.name === 'test');
 *   // allocation.age would cause a TypeScript error
 * }
 *
 * const allocation = [{ name: 'hi' }] as AllocationQuerySchema[];
 * const relatedRecord = [{ name: 'hi' }] as RelatedRecord[];
 *
 * doSomething(allocation); // valid
 * doSomething(relatedRecord); // TS2345: Argument of type RelatedRecord[] is not assignable to parameter of type AllocationCRUDSchema[] | AllocationQuerySchema[]
 */
export type PropertySubsetValidator<T, K extends keyof T> = Pick<T, K>;

export type RefetchQuery<Request, Response> = QueryActionCreatorResult<
  QueryDefinition<
    Request,
    BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
    string,
    Response,
    string
  >
>;

type ClientV2PATCHMutation<T extends GenericResource> = UseMutation<
  MutationDefinition<
    ClientV2PATCHRequest<any>,
    BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
    string,
    ClientV2PATCHResponse<T>
  >
>;

type UnifiedPATCHMutation<T extends GenericResource> = UseMutation<
  MutationDefinition<
    UnifiedPATCHRequest<any>,
    BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
    string,
    UnifiedPATCHResponse<T>
  >
>;

export type PatchMutation<T extends GenericResource> =
  | ClientV2PATCHMutation<T>
  | UnifiedPATCHMutation<T>;

type ClientV2ToggleMutation<T extends GenericResource> = UseMutation<
  MutationDefinition<
    ClientV2ToggleRequest<T>,
    BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
    string,
    ClientV2ToggleResponse<T>
  >
>;

type UnifiedToggleMutation<T extends GenericResource> = UseMutation<
  MutationDefinition<
    UnifiedToggleRequest<T>,
    BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
    string,
    UnifiedToggleResponse<T>
  >
>;

export type ToggleMutation<T extends GenericResource> =
  | ClientV2ToggleMutation<T>
  | UnifiedToggleMutation<T>;

type ClientV2DELETEMutation<T extends GenericResource> = UseMutation<
  MutationDefinition<
    ClientV2DELETERequest<T>,
    BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
    string,
    ClientV2DELETEResponse<T>
  >
>;

type UnifiedDELETEMutation<T extends GenericResource> = UseMutation<
  MutationDefinition<
    UnifiedDELETERequest<T>,
    BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
    string,
    UnifiedDELETEResponse<T>
  >
>;

export type DeleteMutation<T extends GenericResource> =
  | ClientV2DELETEMutation<T>
  | UnifiedDELETEMutation<T>;

type ClientV2GETQuery<T extends GenericResource> = UseQuery<
  QueryDefinition<
    QueryParams<T>,
    BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
    string,
    ListResponse<T>
  >
>;

type UnifiedGETQuery<T extends GenericResource> = UseQuery<
  QueryDefinition<
    UnifiedQueryParams<T>,
    BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
    string,
    ListResponse<T>
  >
>;

export type GetQuery<T extends GenericResource> =
  | ClientV2GETQuery<T>
  | UnifiedGETQuery<T>;
